import { create } from 'zustand'
import { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Profile, Site } from '@/types/database'

interface AuthState {
  user: User | null
  profile: Profile | null
  sites: Site[]
  currentSite: Site | null
  // Legacy alias - always same as currentSite
  site: Site | null
  isLoading: boolean
  isInitialized: boolean

  // Actions
  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  refreshSites: () => Promise<void>
  selectSite: (siteId: string) => void
  // Legacy alias
  refreshSite: () => Promise<void>
}

// Helper to set both site and currentSite
const setSite = (site: Site | null) => ({ currentSite: site, site })

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  sites: [],
  currentSite: null,
  site: null,
  isLoading: true,
  isInitialized: false,

  initialize: async () => {
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        set({ user: session.user })

        // Fetch profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()

        if (profile) {
          set({ profile })

          // Fetch all sites for the user
          const { data: sitesData } = await supabase
            .from('sites')
            .select('*')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: true })

          const sites = (sitesData || []) as Site[]

          // Try to restore last selected site from localStorage
          const lastSiteId = localStorage.getItem('lurker_current_site')
          const currentSite = sites.find(s => s.id === lastSiteId) || sites[0] || null

          set({ sites, ...setSite(currentSite) })
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
    } finally {
      set({ isLoading: false, isInitialized: true })
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        set({ user: session.user })
        await get().refreshProfile()
        await get().refreshSites()
      } else if (event === 'SIGNED_OUT') {
        set({ user: null, profile: null, sites: [], ...setSite(null) })
        localStorage.removeItem('lurker_current_site')
      }
    })
  },

  signIn: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error }
      }

      await get().refreshProfile()
      await get().refreshSites()
      return { error: null }
    } finally {
      set({ isLoading: false })
    }
  },

  signUp: async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) {
      return { error }
    }

    return { error: null }
  },

  signOut: async () => {
    set({ isLoading: true })
    try {
      await supabase.auth.signOut()
      set({ user: null, profile: null, sites: [], ...setSite(null) })
      localStorage.removeItem('lurker_current_site')
    } finally {
      set({ isLoading: false })
    }
  },

  refreshProfile: async () => {
    const { user } = get()
    if (!user) return

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profile) {
      set({ profile })
    }
  },

  refreshSites: async () => {
    const { user, currentSite } = get()
    if (!user) return

    const { data: sitesData } = await supabase
      .from('sites')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })

    const sites = (sitesData || []) as Site[]

    // Keep current selection if still valid, otherwise select first site
    const stillValid = currentSite && sites.some(s => s.id === currentSite.id)
    const newCurrentSite = stillValid ? currentSite : sites[0] || null

    if (newCurrentSite) {
      localStorage.setItem('lurker_current_site', newCurrentSite.id)
    }

    set({ sites, ...setSite(newCurrentSite) })
  },

  selectSite: (siteId: string) => {
    const { sites } = get()
    const site = sites.find(s => s.id === siteId)
    if (site) {
      localStorage.setItem('lurker_current_site', siteId)
      set(setSite(site))
    }
  },

  // Legacy alias for backward compatibility
  refreshSite: async () => {
    await get().refreshSites()
  },
}))

// Initialize auth on import
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize()
}
